import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import ThemeToggle from '../components/ThemeToggle';
import CgmProfileCard from '../components/CgmProfileCard';
import AnalyticsProfilePicker from '../components/AnalyticsProfilePicker';
import LiveGlucoseChart from '../components/LiveGlucoseChart';
import DailyOverlayChart from '../components/DailyOverlayChart';
import { useAnalyticsCgmReadings } from '../hooks/useAnalyticsCgmReadings';
import {
  executeBolusProcedure,
  parseStatisticsRow,
  buildAgpChartData,
  buildDailyOverlayDays,
  toYmd,
  addDays,
  addMonths,
  type GlucoseStatisticsParsed,
  type AgpChartData,
  type DailyOverlayDayParsed,
} from '../services/analyticsApi';
import './Analytics.css';

const ANALYTICS_PROFILE_IDS_KEY = 'bolusnotes_analytics_profile_ids';

const SERIES_COLORS = ['#0a84ff', '#ff9f0a', '#30d158', '#bf5af2', '#64d2ff'];

type ChartMode = 'live' | 'statistics' | 'agp' | 'daily';

const ANALYTIC_RANGES = [
  { id: '1d', label: '1 Day', start: (end: Date) => addDays(end, -1) },
  { id: '3d', label: '3 Days', start: (end: Date) => addDays(end, -3) },
  { id: '1w', label: '1 Week', start: (end: Date) => addDays(end, -7) },
  { id: '2w', label: '2 Weeks', start: (end: Date) => addDays(end, -14) },
  { id: '1m', label: '1 Month', start: (end: Date) => addMonths(end, -1) },
  { id: '3m', label: '3 Months', start: (end: Date) => addMonths(end, -3) },
  { id: '6m', label: '6 Months', start: (end: Date) => addMonths(end, -6) },
  { id: '12m', label: '12 Months', start: (end: Date) => addMonths(end, -12) },
] as const;

type AnalyticRangeId = (typeof ANALYTIC_RANGES)[number]['id'];

/** Matches iOS `TimeRange.hours` in AnalyticsModels.swift */
const LIVE_HOURS_OPTIONS = [
  { h: 1, label: '1 Hour' },
  { h: 3, label: '3 Hours' },
  { h: 6, label: '6 Hours' },
  { h: 12, label: '12 Hours' },
  { h: 24, label: '1 Day' },
  { h: 72, label: '3 Days' },
  { h: 168, label: '1 Week' },
  { h: 336, label: '2 Weeks' },
  { h: 720, label: '1 Month' },
  { h: 2160, label: '3 Months' },
  { h: 4320, label: '6 Months' },
  { h: 8760, label: '12 Months' },
];

function TimeInRangeBarWeb({ tir }: { tir: GlucoseStatisticsParsed }) {
  const segs = [
    { pct: tir.veryLowPct, className: 'tir-very-low' },
    { pct: tir.lowPct, className: 'tir-low' },
    { pct: tir.targetPct, className: 'tir-target' },
    { pct: tir.highPct, className: 'tir-high' },
    { pct: tir.veryHighPct, className: 'tir-very-high' },
  ];
  return (
    <div className="tir-bar" role="img" aria-label="Time in range distribution">
      {segs.map(
        (s, i) =>
          s.pct > 0 && (
            <div key={i} className={`tir-segment ${s.className}`} style={{ width: `${s.pct}%` }} />
          )
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  unit,
  colorClass = '',
}: {
  title: string;
  value: string;
  unit: string;
  colorClass?: string;
}) {
  return (
    <div className="analytics-stat-card">
      <div className="analytics-stat-title">{title}</div>
      <div className={`analytics-stat-value ${colorClass}`}>
        {value}
        <span className="analytics-stat-unit">{unit}</span>
      </div>
    </div>
  );
}

function AgpChart({ data }: { data: AgpChartData }) {
  const w = 720;
  const h = 200;
  const padL = 44;
  const padR = 16;
  const padT = 14;
  const padB = 26;
  const { bandPath, medianPath, y180, y70 } = useMemo(() => {
    const innerW = w - padL - padR;
    const innerH = h - padT - padB;
    const gmin = 40;
    const gmax = 320;
    const n = data.median.length;
    const denom = Math.max(n - 1, 1);
    const xAt = (i: number) => padL + (i / denom) * innerW;
    const yAt = (gv: number) => padT + innerH - ((gv - gmin) / (gmax - gmin)) * innerH;

    const top: string[] = [];
    const bot: string[] = [];
    for (let i = 0; i < n; i++) {
      const lo = data.p25[i];
      const hi = data.p75[i];
      if (lo != null && hi != null) {
        top.push(`${xAt(i).toFixed(1)},${yAt(hi).toFixed(1)}`);
        bot.unshift(`${xAt(i).toFixed(1)},${yAt(lo).toFixed(1)}`);
      }
    }
    const bandPath = top.length > 1 ? `M ${top.join(' L ')} L ${bot.join(' L ')} Z` : '';

    const medPts: string[] = [];
    for (let i = 0; i < n; i++) {
      const m = data.median[i];
      if (m != null) medPts.push(`${xAt(i).toFixed(1)},${yAt(m).toFixed(1)}`);
    }
    const medianPath = medPts.length > 1 ? `M ${medPts.join(' L ')}` : '';
    return { bandPath, medianPath, y180: yAt(180), y70: yAt(70) };
  }, [data]);

  return (
    <div className="agp-wrap">
      <h3 className="analytics-section-title">Ambulatory Glucose Profile (AGP)</h3>
      <p className="analytics-hint">Median (line) and 25th–75th percentile band by time of day.</p>
      <svg className="analytics-agp-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        <text x={4} y={padT + 10} className="analytics-svg-label">
          320
        </text>
        <text x={4} y={h - 6} className="analytics-svg-label">
          40
        </text>
        {bandPath && <path d={bandPath} fill="rgba(10,132,255,0.15)" stroke="none" />}
        <line
          x1={padL}
          x2={w - padR}
          y1={y180}
          y2={y180}
          stroke="rgba(52,199,89,0.4)"
          strokeDasharray="3 3"
        />
        <line
          x1={padL}
          x2={w - padR}
          y1={y70}
          y2={y70}
          stroke="rgba(255,149,0,0.4)"
          strokeDasharray="3 3"
        />
        {medianPath && (
          <path d={medianPath} fill="none" stroke="var(--brand-primary)" strokeWidth={2} strokeLinejoin="round" />
        )}
      </svg>
      <div className="agp-axis-labels">
        <span>12:00a</span>
        <span>6:00a</span>
        <span>12:00p</span>
        <span>6:00p</span>
        <span>12:00a</span>
      </div>
    </div>
  );
}

function AgpChartComparison({
  series,
}: {
  series: { name: string; data: AgpChartData; color: string }[];
}) {
  const w = 720;
  const h = 200;
  const padL = 44;
  const padR = 16;
  const padT = 14;
  const padB = 26;

  const { paths, y180, y70 } = useMemo(() => {
    const innerW = w - padL - padR;
    const innerH = h - padT - padB;
    const gmin = 40;
    const gmax = 320;
    const pathList = series.map(({ data, color, name }) => {
      const n = data.median.length;
      const denom = Math.max(n - 1, 1);
      const xAt = (i: number) => padL + (i / denom) * innerW;
      const yAt = (gv: number) => padT + innerH - ((gv - gmin) / (gmax - gmin)) * innerH;
      const medPts: string[] = [];
      for (let i = 0; i < n; i++) {
        const m = data.median[i];
        if (m != null) medPts.push(`${xAt(i).toFixed(1)},${yAt(m).toFixed(1)}`);
      }
      const medianPath = medPts.length > 1 ? `M ${medPts.join(' L ')}` : '';
      return { name, color, medianPath };
    });
    const innerHVal = h - padT - padB;
    const y180v = padT + innerHVal - ((180 - 40) / 280) * innerHVal;
    const y70v = padT + innerHVal - ((70 - 40) / 280) * innerHVal;
    return { paths: pathList, y180: y180v, y70: y70v };
  }, [series]);

  if (series.length === 0) {
    return <div className="analytics-chart-empty">No AGP data to compare.</div>;
  }

  return (
    <div className="agp-wrap agp-compare-wrap">
      <h3 className="analytics-section-title">Ambulatory Glucose Profile (AGP)</h3>
      <p className="analytics-hint">Median curve per profile (same time-of-day axis).</p>
      <svg className="analytics-agp-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        <text x={4} y={padT + 10} className="analytics-svg-label">
          320
        </text>
        <text x={4} y={h - 6} className="analytics-svg-label">
          40
        </text>
        <line
          x1={padL}
          x2={w - padR}
          y1={y180}
          y2={y180}
          stroke="rgba(52,199,89,0.4)"
          strokeDasharray="3 3"
        />
        <line
          x1={padL}
          x2={w - padR}
          y1={y70}
          y2={y70}
          stroke="rgba(255,149,0,0.4)"
          strokeDasharray="3 3"
        />
        {paths.map(
          (p) =>
            p.medianPath && (
              <path
                key={p.name}
                d={p.medianPath}
                fill="none"
                stroke={p.color}
                strokeWidth={2}
                strokeLinejoin="round"
              />
            )
        )}
      </svg>
      <div className="agp-axis-labels">
        <span>12:00a</span>
        <span>6:00a</span>
        <span>12:00p</span>
        <span>6:00p</span>
        <span>12:00a</span>
      </div>
      <div className="agp-compare-legend">
        {series.map((s) => (
          <span key={s.name} className="agp-compare-legend-item">
            <i className="agp-compare-swatch" style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Hour index 0–23 → column label 12AM … 11PM */
function formatDailyHourColumnLabel(h: number): string {
  if (h === 0) return '12AM';
  if (h < 12) return `${h}AM`;
  if (h === 12) return '12PM';
  return `${h - 12}PM`;
}

function DailyOverlayTable({ days, profileName }: { days: DailyOverlayDayParsed[]; profileName?: string }) {
  const [dateSort, setDateSort] = useState<'asc' | 'desc'>('asc');

  const sortedDays = useMemo(() => {
    const copy = [...days];
    copy.sort((a, b) => (dateSort === 'asc' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)));
    return copy;
  }, [days, dateSort]);

  if (days.length === 0) {
    return <div className="analytics-chart-empty">No daily overlay data for this range.</div>;
  }

  return (
    <div className="daily-overlay-wrap">
      {profileName ? <h2 className="analytics-daily-profile-title">{profileName}</h2> : null}
      <h3 className="analytics-section-title">Daily overlay</h3>
      <p className="analytics-hint">Average glucose by hour (darker = higher). Empty cells = no data.</p>
      <div className="daily-scroll">
        <table className="daily-table">
          <thead>
            <tr>
              <th
                className="daily-th-date"
                scope="col"
                aria-sort={dateSort === 'asc' ? 'ascending' : 'descending'}
              >
                <button
                  type="button"
                  className="daily-date-sort-btn"
                  onClick={() => setDateSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                  aria-label={
                    dateSort === 'asc'
                      ? 'Dates sorted oldest first. Activate to sort newest first.'
                      : 'Dates sorted newest first. Activate to sort oldest first.'
                  }
                  title={dateSort === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}
                >
                  <span>Date</span>
                  <span className="daily-sort-chevron" aria-hidden>
                    {dateSort === 'asc' ? '↑' : '↓'}
                  </span>
                </button>
              </th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} title={formatDailyHourColumnLabel(h)}>
                  {formatDailyHourColumnLabel(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedDays.map((d) => (
              <tr key={d.date}>
                <td className="daily-date">{d.date}</td>
                {d.hours.map((cell) => (
                  <td
                    key={cell.hour}
                    className="daily-cell"
                    title={
                      cell.avg != null ? `${cell.hour}:00 avg ${Math.round(cell.avg)} mg/dL (${cell.count} pts)` : ''
                    }
                    data-level={
                      cell.avg == null ? 'none' : cell.avg < 70 ? 'low' : cell.avg > 180 ? 'high' : 'ok'
                    }
                  >
                    {cell.avg != null ? Math.round(cell.avg) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GlucoseStatisticsPanel({
  profileName,
  statistics,
  rangeBounds,
}: {
  profileName: string;
  statistics: GlucoseStatisticsParsed;
  rangeBounds: { startYmd: string; endYmd: string };
}) {
  return (
    <section className="analytics-panel analytics-compare-panel">
      <h2 className="analytics-panel-title">{profileName} — Glucose Statistics</h2>
      <p className="analytics-meta">
        {rangeBounds.startYmd} → {rangeBounds.endYmd} · {statistics.totalReadings} readings ·{' '}
        {statistics.daysActive.toFixed(1)} active days · CGM active ~{Math.round(statistics.cgmActivePercent)}%
      </p>
      <div className="analytics-stat-grid">
        <StatCard title="GMI (est. A1C)" value={statistics.gmi.toFixed(1)} unit="%" colorClass="stat-purple" />
        <StatCard title="Average" value={String(Math.round(statistics.average))} unit="mg/dL" colorClass="stat-blue" />
        <StatCard title="SD" value={String(Math.round(statistics.sd))} unit="mg/dL" colorClass="stat-orange" />
        <StatCard title="CV" value={statistics.cv.toFixed(1)} unit="%" colorClass="stat-pink" />
        <StatCard title="Median" value={String(Math.round(statistics.median))} unit="mg/dL" colorClass="stat-cyan" />
        <StatCard title="Highest" value={String(Math.round(statistics.highest))} unit="mg/dL" colorClass="stat-red" />
        <StatCard title="Lowest" value={String(Math.round(statistics.lowest))} unit="mg/dL" colorClass="stat-yellow" />
        <StatCard title="Days Active" value={statistics.daysActive.toFixed(1)} unit="days" colorClass="stat-green" />
      </div>
      <div className="tir-section">
        <h3 className="analytics-section-title">Time in Range</h3>
        <TimeInRangeBarWeb tir={statistics} />
        <ul className="tir-legend">
          <li>
            <span className="dot tir-very-high" /> Very High (&gt;250): {statistics.veryHighPct}%
          </li>
          <li>
            <span className="dot tir-high" /> High (181–250): {statistics.highPct}%
          </li>
          <li>
            <span className="dot tir-target" /> Target (70–180): {statistics.targetPct}%
          </li>
          <li>
            <span className="dot tir-low" /> Low (54–69): {statistics.lowPct}%
          </li>
          <li>
            <span className="dot tir-very-low" /> Very Low (&lt;54): {statistics.veryLowPct}%
          </li>
        </ul>
      </div>
    </section>
  );
}

const Analytics: React.FC = () => {
  const { selectedProfile, profiles, loading: profilesLoading } = useProfile();
  const [analyticsProfileIds, setAnalyticsProfileIds] = useState<string[]>([]);
  const analyticsInitRef = useRef(false);

  const location = useLocation();

  const [chartMode, setChartMode] = useState<ChartMode>('live');
  const [analyticRange, setAnalyticRange] = useState<AnalyticRangeId>('1w');
  const [liveHours, setLiveHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statsByProfile, setStatsByProfile] = useState<Record<string, GlucoseStatisticsParsed | null>>({});
  const [agpByProfile, setAgpByProfile] = useState<Record<string, AgpChartData | null>>({});
  const [dailyByProfile, setDailyByProfile] = useState<Record<string, DailyOverlayDayParsed[]>>({});

  const cgmByProfile = useAnalyticsCgmReadings(analyticsProfileIds);

  const analyticsIdsKey = useMemo(() => [...analyticsProfileIds].sort().join(','), [analyticsProfileIds]);

  const rangeBounds = useMemo(() => {
    const end = new Date();
    const def = ANALYTIC_RANGES.find((r) => r.id === analyticRange)!;
    const start = def.start(end);
    return { start, end, startYmd: toYmd(start), endYmd: toYmd(end) };
  }, [analyticRange]);

  useEffect(() => {
    if (profilesLoading || profiles.length === 0) return;

    if (!analyticsInitRef.current) {
      analyticsInitRef.current = true;

      // Check URL parameters directly for isolated profile navigation
      const searchParams = new URLSearchParams(location.search);
      const urlProfileId = searchParams.get('profile');

      if (urlProfileId && profiles.some((p) => p.id === urlProfileId)) {
        setAnalyticsProfileIds([urlProfileId]);
        try {
          localStorage.setItem(ANALYTICS_PROFILE_IDS_KEY, JSON.stringify([urlProfileId]));
        } catch { /* ignore */ }
        return;
      }

      try {
        const raw = localStorage.getItem(ANALYTICS_PROFILE_IDS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as string[];
          const ordered = profiles.filter((p) => parsed.includes(p.id)).map((p) => p.id);
          if (ordered.length > 0) {
            setAnalyticsProfileIds(ordered);
            return;
          }
        }
      } catch {
        /* ignore */
      }
      const fallback =
        selectedProfile && profiles.some((p) => p.id === selectedProfile.id)
          ? selectedProfile.id
          : profiles[0].id;
      setAnalyticsProfileIds([fallback]);
      return;
    }

    setAnalyticsProfileIds((prev) => {
      const next = prev.filter((id) => profiles.some((p) => p.id === id));
      if (next.length === 0 && profiles[0]) return [profiles[0].id];
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
  }, [profilesLoading, profiles, selectedProfile, location.search]);

  const persistAnalyticsProfiles = useCallback(
    (ids: string[]) => {
      const ordered = profiles.filter((p) => ids.includes(p.id)).map((p) => p.id);
      if (ordered.length === 0) return;
      setAnalyticsProfileIds(ordered);
      localStorage.setItem(ANALYTICS_PROFILE_IDS_KEY, JSON.stringify(ordered));
    },
    [profiles]
  );

  const loadAnalyticsModes = useCallback(async () => {
    if (analyticsProfileIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const { startYmd, endYmd } = rangeBounds;
      const stats: Record<string, GlucoseStatisticsParsed | null> = {};
      const agp: Record<string, AgpChartData | null> = {};
      const daily: Record<string, DailyOverlayDayParsed[]> = {};

      await Promise.all(
        analyticsProfileIds.map(async (userId) => {
          const [statsRes, agpRes] = await Promise.all([
            executeBolusProcedure('sp_glucose_statistics', {
              userId,
              startDate: startYmd,
              endDate: endYmd,
            }),
            executeBolusProcedure('sp_glucose_agp', {
              userId,
              startDate: startYmd,
              endDate: endYmd,
            }),
          ]);
          stats[userId] = parseStatisticsRow(statsRes.rows[0] as Record<string, unknown>);
          agp[userId] = buildAgpChartData((agpRes.rows || []) as Record<string, unknown>[]);

          if (chartMode === 'daily') {
            const dailyRes = await executeBolusProcedure('sp_glucose_daily_overlay', {
              userId,
              startDate: startYmd,
              endDate: endYmd,
            });
            daily[userId] = buildDailyOverlayDays((dailyRes.rows || []) as Record<string, unknown>[]);
          }
        })
      );

      if (chartMode !== 'daily') {
        for (const id of analyticsProfileIds) daily[id] = [];
      }

      setStatsByProfile(stats);
      setAgpByProfile(agp);
      setDailyByProfile(daily);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: { message?: string } } }).response?.data?.message : null;
      setError(msg || (e instanceof Error ? e.message : 'Failed to load analytics'));
      setStatsByProfile({});
      setAgpByProfile({});
      setDailyByProfile({});
    } finally {
      setLoading(false);
    }
  }, [analyticsProfileIds, rangeBounds, chartMode]);

  useEffect(() => {
    if (profilesLoading || analyticsProfileIds.length === 0) return;
    if (chartMode === 'live') return;
    loadAnalyticsModes();
  }, [profilesLoading, analyticsIdsKey, chartMode, analyticRange, loadAnalyticsModes, analyticsProfileIds.length]);

  const agpComparisonSeries = useMemo(() => {
    if (chartMode !== 'agp') return [];
    return analyticsProfileIds
      .map((id, i) => {
        const d = agpByProfile[id];
        if (!d) return null;
        return {
          name: profiles.find((p) => p.id === id)?.name ?? id,
          data: d,
          color: SERIES_COLORS[i % SERIES_COLORS.length],
        };
      })
      .filter((x): x is { name: string; data: AgpChartData; color: string } => x != null);
  }, [chartMode, analyticsProfileIds, agpByProfile, profiles]);

  const liveProfileMeta = useMemo(
    () =>
      analyticsProfileIds.map((id, i) => ({
        id,
        name: profiles.find((p) => p.id === id)?.name ?? id,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      })),
    [analyticsProfileIds, profiles]
  );

  const analyticsReady = !profilesLoading && (profiles.length === 0 || analyticsProfileIds.length > 0);

  const navigate = useNavigate();

  return (
    <div className="analytics-page">
      <header className="page-header analytics-header">
        <div className="header-left">
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-back">
            ← Back
          </button>
          <h1>📊 Analytics</h1>
        </div>
        <div className="header-right">
          <ThemeToggle placement="header" />
        </div>
      </header>

      <div className="analytics-toolbar">
        <div className="mode-segment" role="tablist" aria-label="Analytics mode">
          {(
            [
              ['live', 'Live'],
              ['agp', 'AGP'],
              ['daily', 'Daily'],
              ['statistics', 'Statistics'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={chartMode === id}
              className={`mode-btn ${chartMode === id ? 'active' : ''}`}
              onClick={() => setChartMode(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {chartMode === 'live' ? (
          <div className="toolbar-row">
            <label className="toolbar-label">Window</label>
            <select
              className="analytics-select"
              value={liveHours}
              onChange={(e) => setLiveHours(Number(e.target.value))}
            >
              {LIVE_HOURS_OPTIONS.map((o) => (
                <option key={o.h} value={o.h}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="toolbar-row">
            <label className="toolbar-label">Range</label>
            <select
              className="analytics-select"
              value={analyticRange}
              onChange={(e) => setAnalyticRange(e.target.value as AnalyticRangeId)}
            >
              {ANALYTIC_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <AnalyticsProfilePicker
          profiles={profiles}
          selectedIds={analyticsProfileIds}
          onChange={persistAnalyticsProfiles}
        />
      </div>

      {analyticsProfileIds.length > 0 && (
        <div
          className={`analytics-cgm-banner ${analyticsProfileIds.length > 1 ? 'analytics-cgm-banner-multi' : ''}`}
        >
          {analyticsProfileIds.map((id) => {
            const profile = profiles.find((p) => p.id === id);
            return (
              <CgmProfileCard
                key={id}
                profileName={profile?.name ?? id}
                reading={cgmByProfile[id] ?? null}
              />
            );
          })}
        </div>
      )}

      <div className="analytics-content">
        {profilesLoading && <div className="loading-state">Loading profiles…</div>}
        {!analyticsReady && !profilesLoading && (
          <div className="loading-state">Preparing analytics…</div>
        )}
        {analyticsReady && profiles.length === 0 && (
          <div className="analytics-chart-empty">No profiles available. Add a user first.</div>
        )}
        {error && <div className="analytics-error">{error}</div>}
        {loading && analyticsProfileIds.length > 0 && <div className="loading-state">Loading data…</div>}

        {analyticsProfileIds.length > 0 && chartMode === 'live' && (
          <section className="analytics-panel">
            <h2 className="analytics-panel-title">
              Glucose — last {LIVE_HOURS_OPTIONS.find((x) => x.h === liveHours)?.label}
            </h2>
            <div className="chart-card chart-card--live-scroll">
              <LiveGlucoseChart
                key={`${analyticsIdsKey}-${liveHours}`}
                profileMeta={liveProfileMeta}
                liveHours={liveHours}
              />
            </div>
          </section>
        )}

        {!loading &&
          analyticsProfileIds.length > 0 &&
          chartMode === 'statistics' &&
          analyticsProfileIds.map((id) => {
            const profile = profiles.find((p) => p.id === id);
            const statistics = statsByProfile[id];
            if (!statistics) {
              return (
                <div key={id} className="analytics-chart-empty">
                  No CGM readings in this date range for {profile?.name ?? 'this profile'}.
                </div>
              );
            }
            return (
              <GlucoseStatisticsPanel
                key={id}
                profileName={profile?.name ?? id}
                statistics={statistics}
                rangeBounds={rangeBounds}
              />
            );
          })}

        {!loading && analyticsProfileIds.length > 0 && chartMode === 'agp' && (
          <>
            {agpComparisonSeries.length === 0 && !error && (
              <div className="analytics-chart-empty">No AGP data for the selected profile(s).</div>
            )}
            {agpComparisonSeries.length === 1 && (
              <section className="analytics-panel">
                <div className="chart-card">
                  <AgpChart data={agpComparisonSeries[0].data} />
                </div>
              </section>
            )}
            {agpComparisonSeries.length > 1 && (
              <section className="analytics-panel">
                <div className="chart-card">
                  <AgpChartComparison series={agpComparisonSeries} />
                </div>
              </section>
            )}
          </>
        )}

        {!loading &&
          analyticsProfileIds.length > 0 &&
          chartMode === 'daily' &&
          analyticsProfileIds.map((id) => {
            const profile = profiles.find((p) => p.id === id);
            const days = dailyByProfile[id] ?? [];
            return (
              <section key={id} className="analytics-panel analytics-daily-profile-section">
                <div className="chart-card daily-profile-card-wrap">
                  <DailyOverlayChart days={days} />
                  <div style={{ height: '32px' }} />
                  <DailyOverlayTable days={days} profileName={profile?.name} />
                </div>
              </section>
            );
          })}
      </div>
    </div>
  );
};

export default Analytics;
